import { memo, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  PointCloudGradientProperty,
  PointCloudPointSizeProperty,
  PointCloudEdgesProperty,
  PointCloudAppearanceProperty,
  PointCloudHeightRangeProperty,
  PointCloudIntensityRangeProperty,
  PointCloudResolutionProperty,
  PointCloudClassificationFilter,
  PointCloudSourceFilter,
} from '../../Properties/integration/PointCloudPropertyIntegration';
import { RendererContext } from '../../../contexts/RendererContext';
import { ConvertType, useFormatNumber } from '../../../hooks/useFormatNumber';
import { T } from '../../../translation/src';
import { PointCloud } from '../../../types/graphqlTypes';
import { PropertiesSection, Property } from './PropertiesSection';

interface PointCloudPropertiesProps {
  pointCloud: Pick<PointCloud, 'cloudName' | 'hasClassifications' | 'hasSources' | 'availableClasses' | 'availableSources' | 'pointCount' | 'id'>;
}
const PointCloudProperties_: React.FC2<PointCloudPropertiesProps> = ({ pointCloud }) => {
  const { projectId = '' } = useParams();
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;

  const selectedPointCloud = viewer?.scene.pointclouds.find((pc) => pc.identifier === pointCloud.id);

  // Check if intensity data is available
  const noIntensity = !selectedPointCloud?.material?.initialIntensityMax && !selectedPointCloud?.material?.initialIntensityMin;

  const { formatNumber } = useFormatNumber();

  return (
    <div className="flex flex-col flex-grow space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="information" swc />}>
        <Property title={<T _str="type" swc />}>
          <T _str="point cloud" cap />
        </Property>
        <Property title={<T _str="number of points" swc />}>{formatNumber(pointCloud.pointCount || 0, { convertType: ConvertType.NONE, addUnit: false })}</Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="appearance" swc />}>
        <Property title={<T _str="point size" swc />}>
          <PointCloudPointSizeProperty pointCloudId={pointCloud.id} />
        </Property>
        <Property title={<T _str="point edges" swc />}>
          <PointCloudEdgesProperty pointCloudId={pointCloud.id} />
        </Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="Colorization" swc />}>
        <Property title={<T _str="Field" swc />}>
          <PointCloudAppearanceProperty pointCloudId={pointCloud.id} />
        </Property>
        <Property title={<T _str="gradient" swc />} className="mb-2">
          <PointCloudGradientProperty pointCloudId={pointCloud.id} />
        </Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="filters" swc />}>
        <Property title={<T _str="resolution" swc />}>
          <PointCloudResolutionProperty pointCloudId={pointCloud.id} />
        </Property>
        {pointCloud.hasClassifications && (
          <Property title={<T _str="classification" swc />}>
            <PointCloudClassificationFilter pointCloudId={pointCloud.id} availableClasses={pointCloud.availableClasses} projectId={projectId} />
          </Property>
        )}
        {pointCloud.hasSources && (
          <Property title={<T _str="source ids" swc />}>
            <PointCloudSourceFilter pointCloudId={pointCloud.id} availableSources={pointCloud.availableSources} />
          </Property>
        )}
        <Property title={<T _str="height" swc />} className="mb-2">
          <PointCloudHeightRangeProperty pointCloudId={pointCloud.id} />
        </Property>
        {!noIntensity && (
          <Property title={<T _str="intensity" swc />}>
            <PointCloudIntensityRangeProperty pointCloudId={pointCloud.id} />
          </Property>
        )}
      </PropertiesSection>
    </div>
  );
};

export const PointCloudProperties = memo(PointCloudProperties_);
